import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@mui/material";
import { Lms, PlatformInfo } from "@verity/api-client/types";
import { CoreButton } from "@verity/components/core";
import { isSupportedBrowser } from "../helpers/browser";
import { isIncognitoAllowed, isInManagedGuestSession } from "../helpers/extension";
import { Trans } from "react-i18next";
import { isChromeOsDevice } from "../../helpers/device";
import apiClient from "../../helpers/api-client";
import { initiateAuthenticationRedirect } from "../../helpers/oauth";
import i18n from "../../i18n";
import SetupInstructions from "./SetupInstructions";
import SetupPageHeader from "./SetupPageHeader";
import StepCard, { SetupStepList } from "./StepCard";
import headerStyles from "./SetupPageHeader.module.css";
import styles from "./ExtensionSetupInstructions.module.css";
import supportedBrowsers from "../images/supported-browsers.svg";
import browserExtension from "../images/browser-extension.svg";
import incognitoMode from "../images/incognito-mode.svg";
import assessment from "../images/assessment.svg";
import authentication from "../images/authentication.svg";
import managedGuestSession from "../images/managed-guest-session.svg";
import { Match } from "effect";
import { getBrowserName } from "@verity/extension/platform";

type ExtendedLms = Lms | "BlackboardLearnUltra";
type Step = "browser" | "extension" | "managedGuestSession" | "incognito" | "start" | "authentication";

const SETUP_VIDEO_URLS: Record<ExtendedLms, string> = {
	canvas: "https://demo.video.yuja.com/V/Video?v=7996635&node=34050322&a=119537539&autoplay=1",
	moodle: "https://demo.video.yuja.com/V/Video?v=7970134&node=34011281&a=182599116&autoplay=1",
	desire2learn: "https://demo.video.yuja.com/V/Video?v=7969361&node=34009028&a=9290571&autoplay=1",
	BlackboardLearn: "https://demo.video.yuja.com/V/Video?v=8004173&node=34079820&a=141070441&autoplay=1",
	BlackboardLearnUltra: "https://demo.video.yuja.com/V/Video?v=7996847&node=34051565&a=74973534&autoplay=1",
};

/**
 * Map the lms to its appropriate interpolated value
 */
const getLmsInterpolation = (lms?: ExtendedLms) => {
	switch (lms) {
		case "canvas":
			return "Canvas";
		case "moodle":
			return "Moodle";
		case "desire2learn":
			return "D2L";
		case "BlackboardLearn":
			return "Blackboard Learn";
		case "BlackboardLearnUltra":
			return "Blackboard Learn Ultra";
		default:
			return "your LMS";
	}
};

function ExtensionSetupStep({
	title,
	description,
	imageSrc,
	children,
}: {
	title: ReactNode;
	description: ReactNode;
	imageSrc: string;
	children?: ReactNode;
}) {
	return (
		<StepCard
			title={title}
			rightContent={
				<>
					<div className={styles.spacer} />
					<div className={styles.imageContainer}>
						<img className={styles.image} src={imageSrc} alt="" />
					</div>
				</>
			}
		>
			<div style={{ paddingBottom: "10px" }}>{description}</div>
			{children && <div className={styles.stepButtonWrapper}>{children}</div>}
		</StepCard>
	);
}

function DefaultExtensionSetupStep({
	step,
	title,
	description,
	imageSrc,
	children,
	interpolations,
}: {
	step: Exclude<Step, "start">;
	title?: ReactNode;
	description?: ReactNode;
	imageSrc: string;
	children?: ReactNode;
	interpolations?: Record<string, string>;
}) {
	return (
		<ExtensionSetupStep
			title={
				title ?? i18n.t(`setup.components.extensionSetupInstructionsTSX.steps.${step}.incomplete.title`, interpolations)
			}
			description={
				description ??
				(step !== "incognito"
					? i18n.t(
							`setup.components.extensionSetupInstructionsTSX.steps.${step}.incomplete.description`,
							interpolations,
						)
					: i18n.t(`setup.components.extensionSetupInstructionsTSX.steps.incognito.incomplete.description.paragraph`))
			}
			imageSrc={imageSrc}
		>
			{children}
		</ExtensionSetupStep>
	);
}

function DefaultCompleteExtensionSetupStep({
	step,
	interpolations,
}: {
	step: Exclude<Step, "start">;
	interpolations?: Record<string, string>;
}) {
	return (
		<StepCard
			complete
			title={i18n.t(`setup.components.extensionSetupInstructionsTSX.steps.${step}.complete.title`, interpolations)}
		>
			{i18n.t(`setup.components.extensionSetupInstructionsTSX.steps.${step}.complete.description`, interpolations)}
		</StepCard>
	);
}

export default function ExtensionSetupInstructions({
	lms,
	requireExtension = true,
	platformInfo,
	checkIncognitoMode = false,
	managedGuestSessionRequireIncognito = true,
	requireManagedGuestSession = false,
	courseId,
	startAssessment,
	checkCompatibility,
	goToAssessments,
}: {
	lms?: ExtendedLms;
	requireExtension?: boolean;
	platformInfo: Pick<PlatformInfo, "platform" | "versionSupported">;
	checkIncognitoMode?: boolean;
	managedGuestSessionRequireIncognito?: boolean;
	requireManagedGuestSession?: boolean;
	courseId?: number;
	startAssessment?: () => void;
	checkCompatibility?: () => void;
	goToAssessments?: () => void;
}) {
	const [inManagedGuestSession, setInManagedGuestSession] = useState(false);
	useEffect(() => {
		if (platformInfo.platform === "extension") {
			isInManagedGuestSession(import.meta.env.VITE_EXTENSION_ID).then(setInManagedGuestSession);
		} else {
			setInManagedGuestSession(false);
		}
	}, []);

	/**
	 * On prop change:
	 * - Determine the steps the user is shown
	 */
	const steps = useMemo(() => {
		const stepsList: Step[] = [];
		if (requireExtension) {
			stepsList.push("browser", "extension");
			if (isChromeOsDevice() && requireManagedGuestSession) {
				stepsList.push("managedGuestSession");
			}
			if (
				checkIncognitoMode &&
				(managedGuestSessionRequireIncognito ||
					!isChromeOsDevice() ||
					(!inManagedGuestSession && !requireManagedGuestSession))
			) {
				stepsList.push("incognito");
			}
		}
		if (lms === "canvas" && courseId) {
			stepsList.push("authentication");
		}
		if (startAssessment || checkCompatibility || goToAssessments) {
			stepsList.push("start");
		}
		return stepsList;
	}, [
		requireExtension,
		checkIncognitoMode,
		managedGuestSessionRequireIncognito,
		lms,
		courseId,
		startAssessment,
		checkCompatibility,
		goToAssessments,
	]);

	const [currStepIndex, setCurrStepIndex] = useState(0);

	/**
	 * Check incognito mode on every step change
	 *
	 * Implemented via pinging the chrome extension for status updates
	 */
	const [incognitoEnabled, setIncognitoEnabled] = useState(false);
	useEffect(() => {
		const incognitoStepIndex = steps.indexOf("incognito");
		if (incognitoStepIndex >= 0 && currStepIndex >= incognitoStepIndex) {
			const checkIncognito = async () => {
				if (await isIncognitoAllowed(import.meta.env.VITE_EXTENSION_ID)) {
					setIncognitoEnabled(true);
				} else {
					setIncognitoEnabled(false);
				}
			};
			void checkIncognito();
			const interval = setInterval(checkIncognito, 5000);
			return () => clearInterval(interval);
		}
	}, [currStepIndex, steps]);

	/**
	 * Attempt to refresh the users OAuth token. This will fail
	 * if either:
	 * - The refresh token does not exist
	 * - The refresh token is expired
	 *
	 * In either case, the user will be required to authenticate again.
	 */
	const [isUserAuthorized, setIsUserAuthorized] = useState(false);
	useEffect(() => {
		const authenticationStepIndex = steps.indexOf("authentication");
		if (authenticationStepIndex >= 0 && currStepIndex >= authenticationStepIndex) {
			const checkUserAuth = async () => {
				const tokenExists = await apiClient.oauth.refreshStoredToken();
				setIsUserAuthorized(tokenExists);
				console.error("Token exists:", tokenExists);
			};
			void checkUserAuth();
		}
	}, [currStepIndex, steps]);

	/**
	 * Callbacks to check if a step is complete or skipped
	 */
	const checkStepComplete = useCallback(
		(step: Step) => {
			switch (step) {
				case "browser":
					return isSupportedBrowser();
				case "extension":
					return platformInfo.platform === "extension" && platformInfo.versionSupported;
				case "managedGuestSession":
					return inManagedGuestSession;
				case "incognito":
					return incognitoEnabled;
				case "authentication":
					return isUserAuthorized;
				case "start":
					return false;
			}
		},
		[platformInfo, inManagedGuestSession, incognitoEnabled, isUserAuthorized],
	);

	/**
	 * Update the current step index depending on step completion status
	 */
	useEffect(() => {
		let newStepIndex = currStepIndex;
		for (const [index, step] of steps.entries()) {
			if (currStepIndex > index) {
				if (!checkStepComplete(step)) {
					newStepIndex = index;
					break;
				}
			} else if (newStepIndex === index && checkStepComplete(step)) {
				newStepIndex = index + 1;
			}
		}
		if (newStepIndex !== currStepIndex) {
			setCurrStepIndex(newStepIndex);
		}
	}, [currStepIndex, steps, checkStepComplete]);

	/**
	 * Get interpolations for this component
	 */
	const interpolations = useMemo(() => {
		return {
			lms: getLmsInterpolation(lms),
		};
	}, [lms]);

	const startButtonDisabled = currStepIndex < steps.indexOf("start");

	return (
		<SetupInstructions
			header={
				<SetupPageHeader
					title={i18n.t("setup.components.extensionSetupInstructionsTSX.title")}
					description={i18n.t("setup.components.extensionSetupInstructionsTSX.description")}
				>
					<iframe
						className={headerStyles.video}
						title={i18n.t(`setup.components.extensionSetupInstructionsTSX.videoTitles.${lms ?? "canvas"}`)}
						src={SETUP_VIDEO_URLS[lms ?? "canvas"]}
						allowFullScreen
						loading="lazy"
					/>
				</SetupPageHeader>
			}
		>
			<SetupStepList>
				{steps.map((step, index) => {
					const complete = index < currStepIndex;

					if (complete && step !== "start") {
						return <DefaultCompleteExtensionSetupStep key={step} step={step} interpolations={interpolations} />;
					} else {
						switch (step) {
							case "browser":
								return <DefaultExtensionSetupStep key={step} step={step} imageSrc={supportedBrowsers} />;
							case "extension": {
								const installed = platformInfo.platform === "extension";
								const browserName = getBrowserName();

								const getDescription = () => {
									return Match.value({ isFirefox: browserName === "firefox", installed }).pipe(
										Match.when({ isFirefox: true, installed: false }, () =>
											i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.extension.incomplete.descriptionFirefox",
											),
										),
										Match.when({ isFirefox: false, installed: false }, () =>
											i18n.t("setup.components.extensionSetupInstructionsTSX.steps.extension.incomplete.description"),
										),
										Match.when({ isFirefox: true, installed: true }, () =>
											i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.extension.incomplete.updateDescriptionFirefox",
											),
										),
										Match.when({ isFirefox: false, installed: true }, () =>
											i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.extension.incomplete.updateDescription",
											),
										),
										Match.orElse(() => undefined),
									);
								};

								const extensionUrl = Match.value(getBrowserName()).pipe(
									Match.when("firefox", () => import.meta.env.VITE_FIREFOX_EXTENSION_URL),
									Match.orElse(() => import.meta.env.VITE_EXTENSION_URL),
								);
								return (
									<DefaultExtensionSetupStep
										key={step}
										step={step}
										title={
											installed
												? i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.extension.incomplete.updateTitle",
													)
												: undefined
										}
										description={getDescription()}
										imageSrc={browserExtension}
									>
										<CoreButton onClick={() => window.open(extensionUrl, "_blank")}>
											{installed
												? i18n.t("setup.components.extensionSetupInstructionsTSX.steps.extension.incomplete.update")
												: i18n.t("setup.components.extensionSetupInstructionsTSX.steps.extension.incomplete.download")}
										</CoreButton>
									</DefaultExtensionSetupStep>
								);
							}
							case "managedGuestSession":
								return (
									<DefaultExtensionSetupStep
										key={step}
										step={step}
										description={
											<>
												<p>
													{i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.managedGuestSession.incomplete.description",
													)}
												</p>
												<p>
													{i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.managedGuestSession.incomplete.description2",
													)}
												</p>
											</>
										}
										imageSrc={managedGuestSession}
									/>
								);
							case "incognito":
								return (
									<DefaultExtensionSetupStep
										key={step}
										step={step}
										description={
											<div className={styles.stepContainer}>
												<div>
													<div className={styles.stepHeader}>Step 1:</div>{" "}
													{i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.incognito.incomplete.description.step1",
													)}
													{<br />}
												</div>
												<div>
													<div className={styles.stepHeader}>Step 2:</div>{" "}
													{i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.incognito.incomplete.description.step2",
													)}
													{<br />}
												</div>
												<div>
													<div className={styles.stepHeader}>Step 3:</div>{" "}
													{i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.incognito.incomplete.description.step3",
													)}
													{<br />}
												</div>
												<div>
													<Trans
														i18nKey="setup.components.extensionSetupInstructionsTSX.steps.incognito.incomplete.watch"
														components={{
															a: (
																<Link
																	href="https://demo.video.yuja.com/V/Video?v=10441261&node=45639852&a=121156268"
																	target="_blank"
																/>
															),
														}}
													/>
												</div>
											</div>
										}
										imageSrc={incognitoMode}
									/>
								);
							case "authentication":
								return (
									<ExtensionSetupStep
										key={step}
										title={
											isUserAuthorized
												? i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.authentication.complete.title",
														interpolations,
													)
												: i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.authentication.incomplete.title",
														interpolations,
													)
										}
										description={
											isUserAuthorized
												? i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.authentication.complete.description",
														interpolations,
													)
												: i18n.t(
														"setup.components.extensionSetupInstructionsTSX.steps.authentication.incomplete.description",
														interpolations,
													)
										}
										imageSrc={authentication}
									>
										<CoreButton
											onClick={() =>
												courseId &&
												void initiateAuthenticationRedirect({
													courseId,
													callbackUrl: window.location.href,
												})
											}
										>
											{i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.authentication.incomplete.authenticate",
											)}
										</CoreButton>
									</ExtensionSetupStep>
								);
							case "start":
								if (startAssessment) {
									return (
										<ExtensionSetupStep
											key={step}
											title={i18n.t("setup.components.extensionSetupInstructionsTSX.steps.start.startAssessment.title")}
											description={i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.start.startAssessment.description",
											)}
											imageSrc={assessment}
										>
											<CoreButton onClick={startAssessment} disabled={startButtonDisabled}>
												{i18n.t("setup.components.extensionSetupInstructionsTSX.steps.start.startAssessment.button")}
											</CoreButton>
										</ExtensionSetupStep>
									);
								} else if (checkCompatibility) {
									return (
										<ExtensionSetupStep
											key={step}
											title={i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.start.checkCompatibility.title",
											)}
											description={i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.start.checkCompatibility.description",
											)}
											imageSrc={assessment}
										>
											<CoreButton onClick={checkCompatibility} disabled={startButtonDisabled}>
												{i18n.t("setup.components.extensionSetupInstructionsTSX.steps.start.checkCompatibility.button")}
											</CoreButton>
										</ExtensionSetupStep>
									);
								} else if (goToAssessments) {
									return (
										<ExtensionSetupStep
											key={step}
											title={i18n.t("setup.components.extensionSetupInstructionsTSX.steps.start.assessments.title")}
											description={i18n.t(
												"setup.components.extensionSetupInstructionsTSX.steps.start.assessments.description",
											)}
											imageSrc={assessment}
										>
											<CoreButton onClick={goToAssessments} disabled={startButtonDisabled}>
												{i18n.t("setup.components.extensionSetupInstructionsTSX.steps.start.assessments.button")}
											</CoreButton>
										</ExtensionSetupStep>
									);
								}
								return null;
						}
					}
				})}
			</SetupStepList>
		</SetupInstructions>
	);
}
